// AttributionWaterfall.js — Return attribution waterfall chart
import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { calcAttribution, buildWaterfallData } from '../../lib/projections/attribution';

/**
 * Format as compact currency.
 */
function fmtVal(v, ccy) {
  if (v === null || v === undefined) return '--';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  const sym = ccy === 'GBP' ? '\u00a3' : ccy === 'EUR' ? '\u20ac' : '$';
  if (abs >= 1e6) return `${sign}${sym}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}${sym}${(abs / 1e3).toFixed(0)}K`;
  return `${sign}${sym}${abs.toFixed(0)}`;
}

/**
 * Custom tooltip for waterfall chart.
 */
const WaterfallTooltip = ({ active, payload, baseCurrency }) => {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0]?.payload;
  if (!data) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-none p-2 shadow-sm">
      <p className="font-mono text-[10px] text-zinc-500">{data.label}</p>
      <p className="font-mono text-[11px] font-medium tabular-nums" style={{
        color: data.type === 'negative' ? '#dc2626' : data.type === 'positive' ? '#059669' : '#18181b',
      }}>
        {fmtVal(data.value, baseCurrency)}
      </p>
    </div>
  );
};

/**
 * Attribution waterfall chart showing return decomposition.
 * @param {{ result: Object, baseCurrency: string }} props
 */
const AttributionWaterfall = ({ result, baseCurrency = 'USD' }) => {
  const waterfallData = useMemo(() => {
    if (!result || !result.scenarios || !result.scenarios.base) return [];
    const attribution = calcAttribution(
      result.scenarios.base.years,
      result.meta.initialValueBCY
    );
    return buildWaterfallData(attribution);
  }, [result]);

  if (!waterfallData || waterfallData.length === 0) return null;

  const COLORS = {
    total: '#3832e3',
    positive: '#059669',
    negative: '#dc2626',
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5 mb-4">
      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 block mb-4">
        Return Attribution (Base Scenario, Hedged)
      </span>

      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={waterfallData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 8, fontFamily: 'monospace', fill: '#71717a' }}
            angle={-25}
            textAnchor="end"
            height={50}
          />
          <YAxis
            tick={{ fontSize: 9, fontFamily: 'monospace', fill: '#71717a' }}
            tickFormatter={v => fmtVal(v, baseCurrency)}
            width={65}
          />
          <Tooltip content={<WaterfallTooltip baseCurrency={baseCurrency} />} />

          {/* Invisible base bar for waterfall offset */}
          <Bar dataKey="start" stackId="waterfall" fill="transparent" />

          {/* Visible bar segment */}
          <Bar dataKey={(entry) => Math.abs(entry.end - entry.start)} stackId="waterfall" name="Value">
            {waterfallData.map((entry, i) => (
              <Cell key={i} fill={COLORS[entry.type] || COLORS.total} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex gap-4 mt-3">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-[#3832e3]" />
          <span className="font-mono text-[9px] text-zinc-500">Total</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-emerald-600" />
          <span className="font-mono text-[9px] text-zinc-500">Positive</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-red-600" />
          <span className="font-mono text-[9px] text-zinc-500">Negative</span>
        </div>
      </div>
    </div>
  );
};

export default AttributionWaterfall;
